import React, { useState, useEffect } from 'react';
import { Input } from './input';
import { maskCNPJ, cleanCNPJ, validateCNPJ } from '../../utils/cnpjMask';

/**
 * Componente de Input com máscara de CNPJ
 * - Aplica máscara automaticamente durante digitação
 * - Aceita apenas números ao colar
 * - Valida CNPJ em tempo real
 * - Mostra feedback visual de validação
 */
const CNPJInput = React.forwardRef(({ 
  value = '', 
  onChange, 
  onBlur,
  validateDigits = false, // Se deve validar dígitos verificadores
  showValidation = true, // Se deve mostrar feedback visual
  className = '',
  disabled = false,
  required = false,
  placeholder = '00.000.000/0000-00',
  ...props 
}, ref) => {
  const [internalValue, setInternalValue] = useState(value);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);

  // Sincronizar com valor externo
  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(maskCNPJ(value));
    }
  }, [value]);

  // Validar CNPJ
  const validate = (cnpjValue) => {
    if (!cnpjValue || cnpjValue.trim() === '') {
      if (required) {
        setError('CNPJ é obrigatório');
        return false;
      }
      setError(null);
      return true;
    }

    const cleaned = cleanCNPJ(cnpjValue);
    
    // Validar apenas se tiver 14 dígitos
    if (cleaned.length > 0 && cleaned.length < 14) {
      setError(`CNPJ incompleto (${cleaned.length}/14 dígitos)`);
      return false;
    }

    if (cleaned.length === 14) {
      const validation = validateCNPJ(cnpjValue, validateDigits);
      setError(validation.error);
      return validation.valid;
    }

    setError(null);
    return true;
  };

  // Handler de mudança
  const handleChange = (e) => {
    const rawValue = e.target.value;
    const masked = maskCNPJ(rawValue);
    
    setInternalValue(masked);
    
    // Validar se touched
    if (touched && showValidation) {
      validate(masked);
    }

    // Chamar onChange externo com valor limpo e formatado
    if (onChange) {
      const cleaned = cleanCNPJ(masked);
      onChange({
        target: {
          name: e.target.name,
          value: masked,
          cleanedValue: cleaned,
          isValid: cleaned.length === 14 ? validateCNPJ(masked, validateDigits).valid : false
        }
      });
    }
  };

  // Handler de paste - aceita apenas números
  const handlePaste = (e) => {
    e.preventDefault();
    
    const pastedText = e.clipboardData.getData('text');
    const cleaned = cleanCNPJ(pastedText);
    const masked = maskCNPJ(cleaned);
    
    setInternalValue(masked);
    setTouched(true);
    
    // Validar após colar
    if (showValidation) {
      validate(masked);
    }

    // Chamar onChange externo
    if (onChange) {
      onChange({
        target: {
          name: e.target.name,
          value: masked,
          cleanedValue: cleaned,
          isValid: cleaned.length === 14 ? validateCNPJ(masked, validateDigits).valid : false
        }
      });
    }
  };

  // Handler de blur
  const handleBlur = (e) => {
    setTouched(true);
    
    // Validar ao sair do campo
    if (showValidation) {
      validate(internalValue);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  // Handler de keydown - aceita apenas números e teclas de controle
  const handleKeyDown = (e) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    const isNumber = /^\d$/.test(e.key);
    const isAllowed = allowedKeys.includes(e.key);
    const isCtrlCmd = e.ctrlKey || e.metaKey;

    // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (isCtrlCmd && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }

    if (!isNumber && !isAllowed) {
      e.preventDefault();
    }
  };

  // Determinar classes de validação
  const getValidationClasses = () => {
    if (!showValidation || !touched) return '';
    
    const cleaned = cleanCNPJ(internalValue);
    
    if (!internalValue || cleaned.length === 0) {
      return required ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : '';
    }
    
    if (error) {
      return 'border-red-300 focus:border-red-500 focus:ring-red-500';
    }
    
    if (cleaned.length === 14) {
      return 'border-green-300 focus:border-green-500 focus:ring-green-500';
    }
    
    return '';
  };

  return (
    <div className="w-full">
      <Input
        ref={ref}
        type="text"
        value={internalValue}
        onChange={handleChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={18} // XX.XXX.XXX/XXXX-XX = 18 caracteres
        className={`${className} ${getValidationClasses()}`}
        {...props}
      />
      
      {/* Feedback de validação */}
      {showValidation && touched && error && (
        <p className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
      
      {/* Feedback de sucesso */}
      {showValidation && touched && !error && cleanCNPJ(internalValue).length === 14 && (
        <p className="mt-1 text-sm text-green-600">
          ✓ CNPJ válido
        </p>
      )}
      
      {/* Contador de dígitos */}
      {showValidation && internalValue && cleanCNPJ(internalValue).length > 0 && cleanCNPJ(internalValue).length < 14 && (
        <p className="mt-1 text-xs text-gray-500">
          {cleanCNPJ(internalValue).length}/14 dígitos
        </p>
      )}
    </div>
  );
});

CNPJInput.displayName = 'CNPJInput';

export default CNPJInput;
